import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config/firebase';
import {
  InAppNotification,
  RegisterDeviceTokenInput,
  MarkNotificationReadInput,
  ApiResponse,
} from '../types';
import { mapFunctionsError } from '../utils/errors';

export const notificationService = {
  /**
   * 1. Real-time subscription to student's in-app notifications
   */
  subscribeToNotifications(
    studentId: string,
    callback: (notifications: InAppNotification[]) => void,
    onError?: (error: Error) => void
  ) {
    if (!studentId) return () => {};

    const notifRef = collection(db, 'notifications');
    const q = query(
      notifRef,
      where('studentId', '==', studentId),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const items: InAppNotification[] = [];
        snapshot.forEach((doc) => {
          items.push(doc.data() as InAppNotification);
        });
        callback(items);
      },
      (error) => {
        console.error('Notifications subscription error:', error);
        if (onError) onError(error);
      }
    );
  },

  /**
   * 2. Fetch notifications once
   */
  async getNotifications(studentId: string): Promise<ApiResponse<InAppNotification[]>> {
    try {
      const notifRef = collection(db, 'notifications');
      const q = query(
        notifRef,
        where('studentId', '==', studentId),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      const items: InAppNotification[] = [];
      snap.forEach((doc) => {
        items.push(doc.data() as InAppNotification);
      });
      return { success: true, data: items };
    } catch (err: any) {
      console.error('getNotifications error:', err);
      return { success: false, error: err?.message || 'تعذر جلب الإشعارات' };
    }
  },

  /**
   * 3. Mark single notification as read
   */
  async markAsRead(notificationId: string): Promise<ApiResponse<boolean>> {
    try {
      // First attempt direct document update (allowed by strict firestore rules on 'read' key)
      const docRef = doc(db, 'notifications', notificationId);
      await updateDoc(docRef, { read: true });
      return { success: true, data: true };
    } catch (err) {
      // Fallback to callable cloud function
      try {
        const callable = httpsCallable<MarkNotificationReadInput, { success: boolean }>(
          functions,
          'markNotificationRead'
        );
        await callable({ notificationId });
        return { success: true, data: true };
      } catch (callErr: unknown) {
        return { success: false, error: mapFunctionsError(callErr) };
      }
    }
  },

  /**
   * 4. Mark all unread notifications as read
   */
  async markAllAsRead(): Promise<ApiResponse<boolean>> {
    try {
      const callable = httpsCallable<MarkNotificationReadInput, { success: boolean }>(
        functions,
        'markNotificationRead'
      );
      const res = await callable({ markAll: true });
      return { success: true, data: res.data.success };
    } catch (err: unknown) {
      return { success: false, error: mapFunctionsError(err) };
    }
  },

  /**
   * 5. Register Device Token for Push Notifications (FCM)
   */
  async registerDeviceToken(
    input: RegisterDeviceTokenInput
  ): Promise<ApiResponse<{ deviceId: string }>> {
    try {
      const callable = httpsCallable<RegisterDeviceTokenInput, { success: boolean; deviceId: string }>(
        functions,
        'registerDeviceToken'
      );
      const res = await callable(input);
      return { success: true, data: { deviceId: res.data.deviceId } };
    } catch (err: unknown) {
      console.warn('registerDeviceToken error:', err);
      return { success: false, error: mapFunctionsError(err) };
    }
  },
};
